/**
 * Pure client-side IR diff between two snapshots.
 *
 * The plan's `compute_import_diff` lives backend-side and uses the full
 * Git-rename-detection algorithm with fingerprint matching — that's the
 * right tool for *re-imports* where component identity is uncertain.
 * For comparing two snapshots in the same lineage chain (parent → child
 * via mutate_circuit), identity is *certain* — UUIDs match because the
 * mutation service preserves them — so a simple set diff over component
 * ids gives the exact answer with zero ambiguity.
 *
 * The output drives the SnapshotDiffDialog's center column + the
 * highlight overlays on each pane's mini-canvas.
 */
import type { CircuitIR, Component, Net } from "@models/circuit-ir";

export interface ComponentChange {
  componentId: string;
  refdes: string;
  /** Canonical fields the diff cares about. Anything outside this set
   *  is ignored — schematic geometry / extras / placement aren't part
   *  of the snapshot diff readout. */
  changes: Array<{
    field: "value" | "mpn" | "footprint" | "manufacturer" | "dnp";
    before: unknown;
    after: unknown;
  }>;
}

export interface NetTopologyChange {
  netId: string;
  name: string | null;
  added: Array<{ refdes: string; pin: string }>;
  removed: Array<{ refdes: string; pin: string }>;
}

export interface SnapshotDiff {
  componentsAdded: Component[];
  componentsRemoved: Component[];
  componentsChanged: ComponentChange[];
  netsAdded: Net[];
  netsRemoved: Net[];
  netsTopologyChanged: NetTopologyChange[];
  /** Component ids highlighted on the LEFT pane (changed + removed). */
  leftHighlightIds: Set<string>;
  /** Component ids highlighted on the RIGHT pane (changed + added). */
  rightHighlightIds: Set<string>;
  /** True when the two snapshots are semantically identical. */
  isClean: boolean;
}

const TRACKED_FIELDS = [
  "value",
  "mpn",
  "footprint",
  "manufacturer",
  "dnp",
] as const satisfies ReadonlyArray<ComponentChange["changes"][number]["field"]>;

export function diffSnapshots(
  before: CircuitIR,
  after: CircuitIR,
): SnapshotDiff {
  const beforeById = new Map(before.components.map((c) => [c.id, c]));
  const afterById = new Map(after.components.map((c) => [c.id, c]));

  const componentsAdded: Component[] = [];
  const componentsRemoved: Component[] = [];
  const componentsChanged: ComponentChange[] = [];

  for (const [id, c] of beforeById) {
    if (!afterById.has(id)) componentsRemoved.push(c);
  }
  for (const [id, c] of afterById) {
    if (!beforeById.has(id)) {
      componentsAdded.push(c);
      continue;
    }
    const prev = beforeById.get(id);
    if (!prev) continue;
    const changes: ComponentChange["changes"] = [];
    for (const field of TRACKED_FIELDS) {
      const before = prev[field];
      const after = c[field];
      if (before !== after) {
        changes.push({ field, before, after });
      }
    }
    if (changes.length > 0) {
      componentsChanged.push({
        componentId: id,
        refdes: c.refdes || prev.refdes,
        changes,
      });
    }
  }

  // Net diff — paired by id (snapshot mutations preserve net ids the
  // same way they preserve component ids).
  const beforeNetsById = new Map(before.nets.map((n) => [n.id, n]));
  const afterNetsById = new Map(after.nets.map((n) => [n.id, n]));
  const netsAdded: Net[] = [];
  const netsRemoved: Net[] = [];
  const netsTopologyChanged: NetTopologyChange[] = [];

  // Build refdes lookups so net-endpoint changes render with friendly
  // labels instead of raw component UUIDs.
  const refdesByIdAfter = new Map(
    after.components.map((c) => [c.id, c.refdes]),
  );
  const refdesByIdBefore = new Map(
    before.components.map((c) => [c.id, c.refdes]),
  );

  for (const [id, n] of beforeNetsById) {
    if (!afterNetsById.has(id)) netsRemoved.push(n);
  }
  for (const [id, n] of afterNetsById) {
    if (!beforeNetsById.has(id)) {
      netsAdded.push(n);
      continue;
    }
    const prev = beforeNetsById.get(id);
    if (!prev) continue;
    const beforeKeys = new Set(
      prev.endpoints.map((ep) => `${ep.component_id}::${ep.pin_number}`),
    );
    const afterKeys = new Set(
      n.endpoints.map((ep) => `${ep.component_id}::${ep.pin_number}`),
    );
    const added: NetTopologyChange["added"] = [];
    const removed: NetTopologyChange["removed"] = [];
    for (const ep of n.endpoints) {
      const k = `${ep.component_id}::${ep.pin_number}`;
      if (!beforeKeys.has(k)) {
        added.push({
          refdes: refdesByIdAfter.get(ep.component_id) ?? ep.component_id,
          pin: ep.pin_number,
        });
      }
    }
    for (const ep of prev.endpoints) {
      const k = `${ep.component_id}::${ep.pin_number}`;
      if (!afterKeys.has(k)) {
        removed.push({
          refdes: refdesByIdBefore.get(ep.component_id) ?? ep.component_id,
          pin: ep.pin_number,
        });
      }
    }
    if (added.length > 0 || removed.length > 0) {
      netsTopologyChanged.push({
        netId: id,
        name: n.name,
        added,
        removed,
      });
    }
  }

  // Highlight sets — left pane lights up things that changed or were
  // removed (those are what disappeared / mutated); right pane lights
  // up things that changed or were added.
  const leftHighlightIds = new Set<string>();
  for (const c of componentsRemoved) leftHighlightIds.add(c.id);
  for (const c of componentsChanged) leftHighlightIds.add(c.componentId);

  const rightHighlightIds = new Set<string>();
  for (const c of componentsAdded) rightHighlightIds.add(c.id);
  for (const c of componentsChanged) rightHighlightIds.add(c.componentId);

  const isClean =
    componentsAdded.length === 0 &&
    componentsRemoved.length === 0 &&
    componentsChanged.length === 0 &&
    netsAdded.length === 0 &&
    netsRemoved.length === 0 &&
    netsTopologyChanged.length === 0;

  return {
    componentsAdded,
    componentsRemoved,
    componentsChanged,
    netsAdded,
    netsRemoved,
    netsTopologyChanged,
    leftHighlightIds,
    rightHighlightIds,
    isClean,
  };
}
